/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useContext } from 'react';
import {
  type backHandlerProps,
  type openModuleProps,
  SuperAppContext as BaseSuperAppContext,
} from '@airasia-rn/ui';
import { SocialFeedPageProps } from '../pages/SocialFeedPage/types';
import type { Post } from '../services/types';

export type { openModuleProps, backHandlerProps };

type SuperAppContextType = React.ContextType<typeof BaseSuperAppContext>;

/**
 * Social feed props. locale, geoId, deeplink, currency are not part of props;
 * use useAppConfig() where needed.
 */
export type SocialFeedProps = Omit<
  SuperAppContextType,
  'currency' | 'locale' | 'deeplink' | 'geoId'
> &
  SocialFeedPageProps;

type SocialFeedPageContextType = SocialFeedProps;

/**
 * Default value only used when context is consumed without a provider (e.g. tests).
 */
const SocialFeedPageContext = React.createContext<SocialFeedPageContextType>({
  recommendationHashKey: process.env.EXPO_PUBLIC_RECOMMENDATION_HASH_KEY ?? '',
  openModule: () => {},
  backHandler: () => {},
  runFeedAnimation: false,
  maxPostsPerSection: null,
  maxSections: null,
  maxChips: null,
  onSectionHeaderClick: () => {},
  onFeedItemClick: () => {},
  onFooterChipClick: () => {},
});

const useSocialFeedPageContext = () => {
  return useContext(SocialFeedPageContext);
};

export { SocialFeedPageContext, useSocialFeedPageContext };
export type { SocialFeedPageContextType };
